<template>
  <div>
    <!-- Fil d'Ariane (Breadcrumb) accessible -->
    <b-card
      class="mb-3"
      aria-label="Fil d'Ariane de navigation"
    >
      <b-breadcrumb aria-label="Chemin de navigation">
        <b-breadcrumb-item to="/">
          <feather-icon
            icon="HomeIcon"
            size="16"
            class="mr-25"
            aria-hidden="true"
          />
          <span class="align-middle">Accueil</span>
        </b-breadcrumb-item>
        <b-breadcrumb-item :to="{ name: 'manager-list' }">
          Managers
        </b-breadcrumb-item>
        <b-breadcrumb-item :to="{ name: 'manager-show', params: { id: $route.params.id }}">
          Détail manager
        </b-breadcrumb-item>
        <b-breadcrumb-item
          active
          aria-current="page"
        >
          Changer le mot de passe
        </b-breadcrumb-item>
      </b-breadcrumb>
    </b-card>

    <!-- État de chargement -->
    <b-overlay
      :show="isLoading"
      rounded="sm"
      spinner-variant="primary"
      aria-busy="isLoading"
      aria-live="polite"
    >
      <!-- État d'erreur -->
      <b-alert
        v-if="error"
        variant="danger"
        show
        aria-live="assertive"
        aria-label="Erreur lors du changement de mot de passe"
      >
        {{ error }}
      </b-alert>

      <!-- Formulaire de changement de mot de passe -->
      <b-card
        title="Changer le mot de passe"
        aria-label="Formulaire de changement de mot de passe du manager"
      >
        <validation-observer
          ref="formValidator"
          v-slot="{ handleSubmit }"
        >
          <b-form
            aria-label="Formulaire de changement de mot de passe"
            @submit.prevent="handleSubmit(onSubmit)"
          >
            <b-card-text class="mb-3">
              <div
                class="alert alert-info"
                aria-live="polite"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="mr-1"
                  aria-hidden="true"
                />
                <span>Vous êtes en train de modifier le mot de passe pour le manager : <strong>{{ managerName }}</strong>. Veuillez choisir un mot de passe sécurisé (au moins 8 caractères, chiffres, majuscules et caractères spéciaux recommandés).</span>
              </div>
            </b-card-text>

            <!-- Champs de mot de passe -->
            <b-row>
              <!-- Password field -->
              <b-col md="6">
                <b-form-group
                  label="Nouveau mot de passe*"
                  label-for="password"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="password"
                        v-model="passwordForm.password"
                        :type="showPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : passwordStrength > 25"
                        placeholder="Entrez le nouveau mot de passe"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          @click="showPassword = !showPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>

                    <!-- Password strength meter -->
                    <div class="mt-1">
                      <div class="d-flex justify-content-between mb-1">
                        <small>Force du mot de passe:</small>
                        <small class="text-right">{{ passwordStrengthText }}</small>
                      </div>
                      <b-progress
                        :value="passwordStrength"
                        :variant="passwordStrengthClass"
                        max="100"
                        height="8px"
                      />
                    </div>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm Password -->
              <b-col md="6">
                <b-form-group
                  label="Confirmer le mot de passe*"
                  label-for="confirmPassword"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="confirmPassword"
                        v-model="passwordForm.confirm_password"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Confirmez le mot de passe"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          @click="showConfirmPassword = !showConfirmPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <div class="d-flex justify-content-between mt-4">
              <b-button
                variant="outline-secondary"
                :to="{ name: 'manager-show', params: { id: $route.params.id }}"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-1"
                />
                <span>Annuler</span>
              </b-button>

              <b-button
                variant="primary"
                type="submit"
                :disabled="isSubmitting || !canSubmit"
              >
                <feather-icon
                  icon="SaveIcon"
                  class="mr-1"
                />
                <span>Enregistrer</span>
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, confirmed, min } from 'vee-validate/dist/rules'
import storeAuth from '@/store/store'
import {
  BCard,
  BBreadcrumb,
  BBreadcrumbItem,
  BButton,
  BAlert,
  BOverlay,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BCardText,
  BRow,
  BCol,
  BProgress,
} from 'bootstrap-vue'

// Register the validation rules
extend('required', required)
extend('confirmed', confirmed)
extend('min', min)

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BCard,
    BBreadcrumb,
    BBreadcrumbItem,
    BButton,
    BAlert,
    BOverlay,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BCardText,
    BRow,
    BCol,
    BProgress,
  },

  data() {
    return {
      passwordForm: {
        password: '',
        confirm_password: '',
      },
      manager: {},
      isLoading: false,
      isSubmitting: false,
      error: null,
      showPassword: false,
      showConfirmPassword: false,
      user: {},
    }
  },

  computed: {
    managerName() {
      return this.manager.username || ''
    },

    passwordStrength() {
      let strength = 0
      const { password } = this.passwordForm

      if (!password) return 0

      // Length contribution
      if (password.length >= 8) strength += 25

      // Character type contribution
      if (/[A-Z]/.test(password)) strength += 25 // Uppercase
      if (/[0-9]/.test(password)) strength += 25 // Numbers
      if (/[^A-Za-z0-9]/.test(password)) strength += 25 // Special chars

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    canSubmit() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordForm.password === this.passwordForm.confirm_password
        && this.passwordForm.password.length >= 8
      )
    },
  },

  created() {
    this.user = storeAuth.state.user
    // Check if user is admin
    if (this.user.role !== 'admin') {
      this.error = "Vous n'avez pas l'autorisation de changer les mots de passe."
    } else {
      this.getManagerDetails()
    }
  },

  methods: {
    async getManagerDetails() {
      this.isLoading = true
      try {
        const { id } = this.$route.params
        const { data } = await axios.get(`/api/auth/users/managers/${id}/`)
        this.manager = data
      } catch (error) {
        console.error('Error fetching manager details:', error)
        this.error = 'Erreur lors du chargement des informations du manager'
      } finally {
        this.isLoading = false
      }
    },

    async onSubmit() {
      if (!this.canSubmit) return

      this.isSubmitting = true
      try {
        const { id } = this.$route.params
        await axios.post(`/api/auth/users/managers/change-password/${id}/`, this.passwordForm)

        this.showToast('success', 'Mot de passe modifié avec succès')
        this.$router.push({ name: 'manager-show', params: { id } })
      } catch (error) {
        console.error('Error changing password:', error)
        this.showToast('danger', error.response?.data?.error || 'Erreur lors du changement de mot de passe')
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.form-group {
  margin-bottom: 1.5rem;
}

.cursor-pointer {
  cursor: pointer;
}

.input-group-merge.is-invalid .input-group-append {
  border-color: #ea5455;
}

.btn-min-width {
  min-width: 150px;
}

/* Password strength indicator styling */
.progress {
  height: 8px !important;
  border-radius: 4px;
  background-color: #eee;
}

/* Ensure the entire input-group-append is clickable */
.input-group-append.is-text {
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
